import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { usePreload } from "./PreloadContext";
import {
  fetchClasses,
  fetchClassesSimple,
  fetchOcctooClasses,
} from "../../services/classService";
import { fetchGroups } from "../../services/groupService";
import { fetchFeatures } from "../../services/featureService";
import { fetchValues } from "../../services/valueService";
import { useAuth0 } from "@auth0/auth0-react";
import { defaultLanguage } from "../../config/localization";
import { fetchUnits } from "../../services/unitService";
import { fetchFeatureGroups } from "../../services/featureGroupService";
import { setTokenProvider } from "../auth/tokenProvider";
import { fetchAll, fetchAllCursor } from "../../helpers/buildQueryString";
import { after } from "lodash";
import {
  groupsPageCache,
  featureGroupsPageCache,
  classesPageCache,
  featuresPageCache,
  unitsPageCache,
  valuesPageCache,
} from "../../pages/cache/PageCache";

const DataContextt = createContext();

export const DataProvider = ({ children }) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
  } = useAuth0();
  const languageCacheKey = `attrmgmnt_language`;
  const { withLoader, showNotification } = usePreload();
  const [language, setLanguage] = useState(
    localStorage.getItem(languageCacheKey) ?? defaultLanguage,
  );

  const [groups, setGroups] = useState({});
  const [features, setFeatures] = useState({});
  const [classes, setClasses] = useState({});
  const [values, setValues] = useState({});
  const [units, setUnits] = useState({});
  const [featureGroups, setFeatureGroups] = useState({});
  const pageSize = 25;
  const cacheKey = `${language}-0-${pageSize}-`;
  const inFlightRequest = useRef({});

  const updateLanguage = async (code) => {
    setLanguage(code);
    localStorage.setItem(languageCacheKey, code);
  };

  const loadGroups = useCallback(async () => {
    if (inFlightRequest.current.groups) return;

    if (groups?.results?.length && groups?.language === language) {
      return groups;
    }

    inFlightRequest.current.groups = true;

    const data = await fetchAll(({ skip, top }) =>
      fetchGroups(
        `skip=${skip}&top=${top}&includeTotals=true&language=${language}`,
      ),
    );
    //const data = await fetchGroups(`includeTotals=true&language=${language}`);
    const result = {
      data: data.results?.slice(0, pageSize) ?? [],
      total: data.total ?? 0,
    };
    groupsPageCache.set(cacheKey, result);
    setGroups({
      ...data,
      language,
    });

    inFlightRequest.current.groups = false;
  }, [language, groups]);

  const loadFeatures = useCallback(async () => {
    if (inFlightRequest.current.features) return;

    if (features?.results?.length && features?.language === language)
      return features;

    inFlightRequest.current.features = true;
    const data = await fetchFeatures(
      `top=1000&includeTotals=true&language=${language}&sortAsc=id`,
    );
    //const data = await fetchAllCursor(fetchFeatures, {top: 9500, idField: "id", language: language});

    const result = {
      data: data.results?.slice(0, pageSize) ?? [],
      total: data.total ?? 0,
    };
    featuresPageCache.set(cacheKey, result);
    setFeatures({
      ...data,
      language,
    });
    inFlightRequest.current.features = false;
  }, [language, features]);

  const loadClasses = useCallback(async () => {
    if (inFlightRequest.current.classes) return;

    if (classes?.results?.length && classes?.language === language)
      return classes;

    inFlightRequest.current.classes = true;
    /*const data = await fetchAll(({ skip, top }) =>
            fetchClasses(`skip=${skip}&top=${top}&includeTotals=true&language=${language}`)
        );*/

    /*const data = await fetchClasses(
      `includeTotals=true&language=${language}&sortAsc=id`,
    );*/
    const data = await fetchClassesSimple(
      `top=9999&skip=0&includeTotals=true&language=${language}&sortAsc=id`,
    );
    const result = {
      data: data.results.slice(0, pageSize) ?? [],
      total: data.total ?? 0,
    };
    classesPageCache.set(cacheKey, result);
    setClasses({
      ...data,
      language,
    });
    inFlightRequest.current.classes = false;
  }, [language, classes]);

  const loadValues = useCallback(async () => {
    if (inFlightRequest.current.values) return;
    if (values?.results?.length && values?.language === language) return values;

    inFlightRequest.current.values = true;
    const data = await fetchValues(
      `top=1000&includeTotals=true&language=${language}&sortAsc=id`,
    );
    const result = {
      data: data.results.slice(0, pageSize) ?? [],
      total: data.total ?? 0,
    };
    valuesPageCache.set(cacheKey, result);
    setValues({
      ...data,
      language,
    });
    inFlightRequest.current.values = false;
  }, [language, values]);

  const loadUnits = useCallback(async () => {
    if (inFlightRequest.current.units) return;
    if (units?.results?.length && units?.language === language) return units;

    inFlightRequest.current.units = true;
    const data = await fetchAll(({ skip, top }) =>
      fetchUnits(
        `skip=${skip}&top=${top}&includeTotals=true&language=${language}&sortAsc=id`,
      ),
    );

    const result = {
      data: data.results.slice(0, pageSize) ?? [],
      total: data.total ?? 0,
    };
    unitsPageCache.set(cacheKey, result);
    setUnits({
      ...data,
      language,
    });

    inFlightRequest.current.units = true;
  }, [language, units]);

  const loadFeatureGroups = useCallback(async () => {
    if (inFlightRequest.current.featureGroups) return;
    if (featureGroups?.results?.length && featureGroups?.language === language)
      return featureGroups;
    inFlightRequest.current.featureGroups = true;
    const data = await fetchFeatureGroups(
      `includeTotals=true&language=${language}&sortAsc=id`,
    );
    const result = {
      data: data.results.slice(0, pageSize) ?? [],
      total: data.total ?? 0,
    };
    featureGroupsPageCache.set(cacheKey, result);
    setFeatureGroups({
      ...data,
      language,
    });
    inFlightRequest.current.featureGroups = true;
  }, [language, featureGroups]);

  const loaders = {
    groups: loadGroups,
    features: loadFeatures,
    classes: loadClasses,
    values: loadValues,
    units: loadUnits,
    featureGroups: loadFeatureGroups,
  };

  const reload = async (sources = "all") => {
    setTokenProvider(getAccessTokenSilently);

    let tasks = [];

    if (sources === "all") {
      tasks = Object.values(loaders).map((loader) => loader());
    } else if (Array.isArray(sources)) {
      tasks = sources
        .map((src) => loaders[src])
        .filter(Boolean)
        .map((loader) => loader());
    } else {
      const loader = loaders[sources];
      if (loader) tasks.push(loader());
    }

    await Promise.all(tasks);
  };

  const preloadAll = useCallback(async () => {
    await reload("all");
    localStorage.setItem(languageCacheKey, language);
    showNotification("Data loaded successfully", "success");
  }, [language]);

  useEffect(() => {
    const preload = async () => {
      if (!isLoading && isAuthenticated) {
        try {
          // Set token provider
          setTokenProvider(getAccessTokenSilently);

          // Preload all data
          await withLoader(preloadAll);
        } catch (err) {
          console.error("Token error or API error:", err);
        }
      }
    };

    //preload();
  }, [
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    withLoader,
    preloadAll,
    loginWithRedirect,
  ]);

  return (
    <DataContextt.Provider
      value={{
        groups,
        features,
        classes,
        values,
        units,
        featureGroups,
        language,
        updateLanguage,
        reload: (sources) => withLoader(() => reload(sources)),
        withLoader,
        setGroups,
        setFeatures,
        setClasses,
        setValues,
        setUnits,
        setFeatureGroups,
      }}
    >
      {children}
    </DataContextt.Provider>
  );
};

export const useData = () => useContext(DataContextt);
